/**
 * Template geometry
 *
 * A print area is physical: it has a width and a height in millimetres.
 * Where that area sits on a mockup image is a property of the mockup
 * template: a pixel scale (pixels per millimetre at the template's native
 * resolution) and two anchors. This module is the one formula that turns
 * the two into the pixel rectangle a consumer draws, clips or renders
 * against, and its inverse, which turns a rectangle an admin drew into a
 * calibration (R7, R8).
 *
 * No consumer recomputes the rectangle: the creator, the server renderer,
 * the admin editor and the partner export all call `templateRect`. See
 * docs/plans/2026-10-22-001-feat-print-area-mm-source-of-truth-plan.md.
 */

/** Calibration states of a mockup template (mirrors the Prisma enum). */
export const CALIBRATION_STATUSES = [
  'CALIBRATED',
  'UNCALIBRATED',
  'NEEDS_REVIEW',
] as const;

export type CalibrationStatus = (typeof CALIBRATION_STATUSES)[number];

/** Who wrote a calibration (mirrors the Prisma enum). */
export const CALIBRATION_SOURCES = ['SYNC', 'ADMIN', 'MIGRATION'] as const;

export type CalibrationSource = (typeof CALIBRATION_SOURCES)[number];

export function isCalibrationStatus(
  value: unknown
): value is CalibrationStatus {
  return (
    typeof value === 'string' &&
    (CALIBRATION_STATUSES as readonly string[]).includes(value)
  );
}

/** The physical size of a print area. */
export interface PrintAreaMm {
  maxWidthMm: number;
  maxHeightMm: number;
}

/**
 * The calibration of one mockup template: how many image pixels one
 * millimetre covers, and where the print area sits. All values refer to
 * the template's native image width (`mockupWidthPx`).
 */
export interface TemplateCalibration {
  /** Image pixels per millimetre at the native resolution */
  pxPerMm: number;
  /** Centre of the print area relative to the image centre, in pixels */
  offsetXCenterPx: number;
  /** Top edge of the print area, in pixels from the image top */
  offsetYTopPx: number;
  /** Native image width the calibration refers to */
  mockupWidthPx: number;
}

/** A pixel rectangle on a mockup image, rounded to whole pixels. */
export interface TemplateRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The pixel rectangle of a print area on a template.
 *
 * Scales to `targetWidthPx` first (rendering assets are wider than the
 * creator image) and rounds each value afterwards, so a rectangle at
 * twice the width is not twice a rounded rectangle (AE7).
 */
export function templateRect(
  area: PrintAreaMm,
  calibration: TemplateCalibration,
  targetWidthPx: number = calibration.mockupWidthPx
): TemplateRect {
  const k = targetWidthPx / calibration.mockupWidthPx;
  const width = area.maxWidthMm * calibration.pxPerMm * k;
  const height = area.maxHeightMm * calibration.pxPerMm * k;
  const x =
    (calibration.mockupWidthPx / 2 + calibration.offsetXCenterPx) * k -
    width / 2;
  const y = calibration.offsetYTopPx * k;

  return {
    x: roundPx(x),
    y: roundPx(y),
    width: roundPx(width),
    height: roundPx(height),
  };
}

/**
 * Math.round without a negative zero (Math.round(-0.5) is -0). NaN stays
 * NaN, so a corrupt input is never laundered into a plausible 0.
 */
export function roundPx(value: number): number {
  const rounded = Math.round(value);
  return rounded === 0 ? 0 : rounded;
}

/** The rectangle an admin draws: left edge, top edge and width in native pixels. */
export interface DrawnRect {
  x: number;
  y: number;
  width: number;
}

/** A print area as the catalogue row carries it, millimetres possibly unset. */
export interface PrintAreaMmInput {
  maxWidthMm?: number | null;
  maxHeightMm?: number | null;
}

export type CalibrationRefusal =
  | { ok: false; reason: 'missing_mm'; field: keyof PrintAreaMm }
  | { ok: false; reason: 'invalid_rect'; field: 'width' | 'mockupWidthPx' };

export type CalibrationResult =
  | { ok: true; calibration: TemplateCalibration }
  | CalibrationRefusal;

/**
 * The calibration a drawn rectangle implies (R8): pixel scale from the
 * drawn width over the millimetre width, anchors from the position. The
 * height is never an input; it follows from the millimetres. Without both
 * millimetre values there is nothing to calibrate against, and the refusal
 * names the missing field (R9).
 */
export function calibrationFromRect(
  area: PrintAreaMmInput,
  rect: DrawnRect,
  mockupWidthPx: number
): CalibrationResult {
  if (!isFinitePositive(area.maxWidthMm)) {
    return { ok: false, reason: 'missing_mm', field: 'maxWidthMm' };
  }
  if (!isFinitePositive(area.maxHeightMm)) {
    return { ok: false, reason: 'missing_mm', field: 'maxHeightMm' };
  }
  if (!isFinitePositive(rect.width)) {
    return { ok: false, reason: 'invalid_rect', field: 'width' };
  }
  if (!isFinitePositive(mockupWidthPx)) {
    return { ok: false, reason: 'invalid_rect', field: 'mockupWidthPx' };
  }

  return {
    ok: true,
    calibration: {
      pxPerMm: rect.width / area.maxWidthMm,
      offsetXCenterPx: rect.x + rect.width / 2 - mockupWidthPx / 2,
      offsetYTopPx: rect.y,
      mockupWidthPx,
    },
  };
}

/** The calibration fields a consumer reads from a template row. */
export interface CalibrationFields {
  calibrationStatus?: CalibrationStatus | null;
  pxPerMm?: number | null;
}

/**
 * Whether a template may be shown, rendered and exported (R12): the status
 * says CALIBRATED and the pixel scale is usable.
 */
export function isCalibrated(template: CalibrationFields): boolean {
  return (
    template.calibrationStatus === 'CALIBRATED' &&
    isFinitePositive(template.pxPerMm)
  );
}

/** A finite number above zero: the only kind a length or a scale may be. */
export function isFinitePositive(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
